import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Alert, 
  Table, 
  THead, 
  TBody, 
  Tr, 
  Th, 
  Td,
  Spinner,
  Pagination,
  Input,
} from '@/components/ui';
import Select from '@/components/ui/Select';

import { 
  HiArrowLeft, 
  HiRefresh,
  HiInformationCircle,
  HiSearch
} from 'react-icons/hi';
import { useRole } from '@/hooks/useRole';
import { 
  fetchOperationData, 
  OperationData, 
  OperationDataListData 
} from '@/services/api/operationDataService';
import { getCommandDescription, COMMAND_DESCRIPTIONS } from '@/constants/command.constant';

// Helper function to format operation type
const formatOperationType = (type: number): string => {
  switch (type) {
    case 0:
      return 'Config';
    case 1:
      return 'Control';
    default:
      return `Unknown (${type})`;
  }
};

// Helper function to format operation status
const formatOperationStatus = (status: number): { label: string; color: string } => {
  switch (status) {
    case 0:
      return { label: 'Failed', color: 'bg-danger-500 text-white' };
    case 1:
      return { label: 'Success', color: 'bg-success-500 text-white' };
    default:
      return { label: `Unknown (${status})`, color: 'bg-gray-500 text-white' };
  }
};

// Helper function to format response type
const formatResponseType = (isResponse: number): string => {
  return isResponse === 1 ? 'Response' : 'Request';
};

// Helper function to format data for display
const formatDataForDisplay = (data: any): string => {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return JSON.stringify(data, null, 2);
  }
};

// Helper function to get data summary for quick view
const getDataSummary = (data: any): string => {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    const keys = Object.keys(parsed);
    if (keys.length <= 2) {
      return Object.entries(parsed).map(([key, value]) => `${key}: ${value}`).join(', ');
    }
    return `${keys.length} fields: ${keys.slice(0, 2).join(', ')}...`;
  } catch {
    return 'Invalid JSON data';
  }
};

// Helper function to format request data for display
const formatRequestDataForDisplay = (data: any): JSX.Element | string => {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    const filteredEntries = Object.entries(parsed).filter(([key]) => key !== 'trackingId');
    
    if (filteredEntries.length === 0) {
      return 'No data fields';
    }
    
    return (
      <div className="text-sm text-gray-800 dark:text-gray-200">
        {filteredEntries.map(([key, value]) => (
          <div key={key} className="mb-1">
            <span className="font-medium text-gray-700 dark:text-gray-300">{key}:</span>{' '}
            <span className="text-gray-900 dark:text-gray-100">{String(value)}</span>
          </div>
        ))}
      </div>
    );
  } catch {
    return 'Invalid JSON data';
  }
};

// Helper function to check response status
const getResponseStatus = (data: any): string | null => {
  try {
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data);
    return dataStr.includes('SUCCESS') ? 'success' : 'fail';
  } catch {
    return null;
  }
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Options for cmd Select (searchable)
const cmdOptions = Object.keys(COMMAND_DESCRIPTIONS).map((commandKey) => ({
  value: commandKey,
  label: getCommandDescription(commandKey),
}));

const OperationDataPage: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isRoleLoading } = useRole();
  
  const [operations, setOperations] = useState<OperationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOperations, setTotalOperations] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    startTime: '',
    endTime: '',
    cmd: ''
  });

  // Load operation data from API
  const loadOperationData = async () => {
    if (!isAdmin() || !deviceId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        page: page + 1, // API uses 1-based indexing
        pageSize: rowsPerPage,
        deviceId: deviceId,
        isResponse: 0, // Always query Request type only
      };

      // Add filters if they are set
      if (filters.startTime) {
        params.startTime = new Date(filters.startTime).toISOString();
      }
      if (filters.endTime) {
        params.endTime = new Date(filters.endTime).toISOString();
      }
      if (filters.cmd) {
        params.cmd = filters.cmd;
      }

      const response: OperationDataListData = await fetchOperationData(params);
      
      setOperations(response.operations);
      setTotalOperations(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError('Failed to load operation data. Please try again later.');
      console.error('Error loading operation data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load operation data on initial render and when pagination, filters, or deviceId changes
  useEffect(() => {
    if (isAdmin() && deviceId) {
      loadOperationData();
    }
  }, [page, rowsPerPage, deviceId, isAdmin, filters]);

  // Handle page change
  const handleChangePage = (newPage: number) => {
    setPage(newPage - 1); // Convert from 1-based to 0-based indexing
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadOperationData();
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/operation');
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      startTime: '',
      endTime: '',
      cmd: ''
    });
    setPage(0);
  };

  // Display loading state
  if (isRoleLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={40} />
      </div>
    );
  }

  // If not admin, show access denied
  if (!isAdmin()) {
    return (
      <div className="p-4">
        <h4 className="text-2xl text-danger-500">Access Denied</h4>
        <p className="mt-2">
          You do not have permission to view this page. This page is only accessible to administrators.
        </p>
      </div>
    );
  }

  // Render operation data table
  return (
    <div className="w-full p-4">
      <div className="flex items-center mb-4">
        <Button 
          variant="plain" 
          icon={<HiArrowLeft />} 
          onClick={handleBack} 
          className="mr-2"
        />
        <h4 className="text-2xl flex-grow">Operation Data for Device: {deviceId}</h4>
        <Button 
          variant="plain" 
          icon={<HiRefresh />} 
          onClick={handleRefresh} 
          disabled={loading}
        />
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <div className="p-3">
          <h5 className="text-lg mb-2">Filters</h5>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Start Time Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">Start Time</label>
              <Input
                type="datetime-local"
                value={filters.startTime}
                onChange={(e) => handleFilterChange('startTime', e.target.value)}
              />
            </div>

            {/* End Time Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">End Time</label>
              <Input
                type="datetime-local"
                value={filters.endTime}
                onChange={(e) => handleFilterChange('endTime', e.target.value)}
              />
            </div>

            {/* Command Filter */}
            <div>
              <label className="block text-sm font-medium mb-1">Command</label>
              <Select
                className="w-full"
                size="sm"
                isClearable
                placeholder="All Commands"
                options={cmdOptions}
                value={filters.cmd ? cmdOptions.find(opt => opt.value === filters.cmd) ?? null : null}
                // react-select style onChange returns option or null
                onChange={(option) => handleFilterChange('cmd', option ? (option as { value: string }).value : '')}
              />
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button 
                variant="plain" 
                onClick={handleClearFilters}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Error message */}
      {error && (
        <Alert className="mb-4" type="danger" showIcon>
          {error}
        </Alert>
      )}

      {/* Operation data table */}
      <Card>
        {loading ? (
          <div className="flex justify-center items-center p-4 h-52">
            <Spinner size={40} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <Tr>
                    <Th className="w-80">Command</Th>
                    <Th className="w-24">Account</Th>
                    <Th className="w-20">Source</Th>
                    <Th className="w-20">Status</Th>
                    <Th className="w-56">Time</Th>
                    <Th className="flex-1 min-w-64">Request Data</Th>
                    <Th className="w-40 min-w-40">RAW Data</Th>
                  </Tr>
                </THead>
                <TBody>
                  {operations.length > 0 ? (
                    operations.map((operation) => {
                      const statusInfo = formatOperationStatus(operation.status);
                      return (
                        <Tr key={operation.id}>
                          <Td className="w-80 whitespace-nowrap">{getCommandDescription(operation.cmd)}</Td>
                          <Td className="w-24">{operation.account}</Td>
                          <Td className="w-20">{operation.source}</Td>
                          <Td className="w-20">
                            <span className={`px-2 py-1 rounded-full text-xs ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </Td>
                          <Td className="w-56 whitespace-nowrap">{formatTimestamp(operation.created_at)}</Td>
                          <Td className="flex-1 min-w-64">
                            {operation.is_response === 0 ? (
                              // Request type: show parsed JSON fields line by line (excluding trackingId)
                              formatRequestDataForDisplay(operation.data)
                            ) : (
                              // Response type: show summary only if it doesn't contain [object Object]
                              !(getDataSummary(operation.data).includes('[object Object]')) && (
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                  {getDataSummary(operation.data)}
                                </div>
                              )
                            )}
                          </Td>
                          <Td className="w-40 min-w-40">
                            <details className="group">
                              <summary className="cursor-pointer text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm whitespace-nowrap">
                                View RAW Data
                              </summary>
                              <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border dark:border-gray-700 text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto dark:text-gray-100">
                                {formatDataForDisplay(operation.data)}
                              </div>
                            </details>
                          </Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={7} className="text-center">
                        No operation data found for this device
                      </Td>
                    </Tr>
                  )}
                </TBody>
              </Table>
            </div>
            
            {/* Pagination */}
            <div className="flex justify-end p-4">
              <Pagination
                currentPage={page + 1}
                total={totalOperations}
                pageSize={rowsPerPage}
                onChange={handleChangePage}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default OperationDataPage;